import { useMediaQuery, useTheme } from "@mui/material";
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useStartSession } from "@trainwell/features/analytics";
import { useTeam } from "@trainwell/features/freemium-plans";
import { useEffect, useState } from "react";
import BasicLayout from "src/components/BasicLayout";
import { useButtonStore } from "src/components/ButtonState";
import { useJoinState } from "src/components/join/JoinState";
import { useUserState } from "src/components/UserState";
import { z } from "zod";

const searchSchema = z.object({
  invited_team_id: z.string().optional(),
  referring_user_id: z.string().optional(),
});

export const Route = createFileRoute("/join/_layout")({
  validateSearch: zodValidator(searchSchema),
  component: LayoutComponent,
});

function LayoutComponent() {
  const theme = useTheme();

  const navigate = useNavigate();
  const location = useLocation();
  const selections = useUserState((state) => state.selections);
  const setTempUserId = useUserState((state) => state.setTempUserId);

  // Decide which page is next.
  const nextPage = () => {
    const currentPath = location.pathname;
    if (currentPath === "/join") {
      void navigate({ to: "/join/isNew" });
    } else if (currentPath === "/join/isNew") {
      // First time user follow account creation.
      if (selections["/join/isNew"][0].id == "first_time") {
        void navigate({ to: "/" });
      } else {
        // Other users follow invite code to join the team.
        void navigate({ to: "/join/inviteCode" });
      }
    }
  };

  const { invited_team_id, referring_user_id } = Route.useSearch();

  // Skip the API call if we don't have both required parameters
  const { data: team } = useTeam({
    teamId: invited_team_id ?? "",
    userId: referring_user_id ?? "",
    queryConfig: {
      enabled: Boolean(invited_team_id && referring_user_id),
    },
  });

  const { data, error } = useStartSession({
    startSessionData: {
      referrerUserId: referring_user_id ?? undefined,
      platform: "web_freemium",
    },
    queryConfig: {
      // Only start a new session if there is no user ID in session storage.
      enabled:
        !sessionStorage.getItem("tempUserId") &&
        !sessionStorage.getItem("userId"),
    },
  });

  useEffect(() => {
    if (data) {
      console.log("Session started:", data);
      setTempUserId(data.userId);
      sessionStorage.setItem("tempUserId", data.userId);
    }
    if (error) {
      console.error("Error starting session:", error);
    }
  }, [data, error, setTempUserId]);

  useEffect(() => {
    if (team) {
      useJoinState.setState({
        teamName: team.team_name,
        invitingUserName: team.inviting_user_name,
        invitingUserHeadshotUrl: team.inviting_user_headshot_url,
        userCount: team.user_count,
        code: team.code,
      });
    }
  }, [team]);

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [buttonText, setButtonText] = useState("Continue");
  const enableButton = useButtonStore((state) => state.isButtonEnabled);
  const isButtonVisible = useButtonStore((state) => state.isVisible);
  const setVisible = useButtonStore((state) => state.setVisible);

  useEffect(() => {
    const currentPath = location.pathname;

    // Decide which image to show on the left side of the screen.
    if (currentPath === "/join") {
      setVisible(true);
      setImageUrl("/assets/onboarding-images/page-1.jpg");
      setButtonText("Accept Invite");
    } else if (currentPath === "/join/isNew") {
      setVisible(true);
      setImageUrl("/assets/onboarding-images/page-1.jpg");
      setButtonText("Continue");
    } else if (currentPath === "/join/inviteCode") {
      setVisible(false);
      setImageUrl("/assets/onboarding-images/page-1.jpg");
    }
  }, [location.pathname, setVisible, setImageUrl, setButtonText]);

  return (
    <BasicLayout
      imageUrl={imageUrl}
      isDesktop={isDesktop}
      buttonText={buttonText}
      isButtonVisible={isButtonVisible}
      enableButton={enableButton}
      onNextPage={nextPage}
      theme={theme}
    >
      <Outlet />
    </BasicLayout>
  );
}
